/**
 * Care Phase Utilities
 *
 * Pure functions to determine the Dashboard state from plant care data.
 * No side effects, no API calls — all inputs are passed explicitly.
 *
 * Used by DashboardContext to compute:
 *   state '0' — no plants placed
 *   state 'A' — plants present, no active care phase
 *   state 'B' — at least one plant in active care phase
 *   state 'F' — many plants in an active care phase at once (high complexity)
 *
 * Also used by PflegekalenderView via getCareTimeline() and formatTimeRange().
 */

/**
 * Complexity threshold for Dashboard State F (#568): the number of plants that
 * must be in an active care phase *at the same time* before the Dashboard
 * switches from the per-plant State B view to the calming collective State F
 * ("Ordnung statt Liste"). Below this, State B handles small counts.
 *
 * This is an internal threshold only — it is NEVER rendered as a number to the
 * user ("Kein Zählen, kein '5 Aufgaben'"). Centralised here so the boundary is
 * configurable in one place.
 */
export const COMPLEXITY_THRESHOLD = 5;

/**
 * Maps English timing enum values (from the plant database) and German
 * seasonal terms to arrays of months (1 = January … 12 = December).
 *
 * English enums come from care_instructions.pruning_best_time and
 * care_instructions.planting_time.
 */
const TIMING_TO_MONTHS = {
  // English enums (plant database)
  early_spring: [2, 3, 4],
  late_spring: [4, 5, 6],
  early_summer: [6, 7],
  late_summer: [7, 8, 9],
  summer: [6, 7, 8],
  autumn: [9, 10, 11],
  late_autumn: [10, 11],
  late_winter: [12, 1, 2],
  winter: [12, 1, 2],
  anytime_if_needed: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  anytime_frost_free: [3, 4, 5, 6, 7, 8, 9, 10],
  anytime: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  spring: [3, 4, 5],
  // German seasonal terms
  frühling: [3, 4, 5],
  frühjahr: [3, 4, 5],
  fruehjahr: [3, 4, 5],
  sommer: [6, 7, 8],
  herbst: [9, 10, 11],
  ganzjährig: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  ganzjaehrig: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  immer: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
};

/**
 * Parses a single numeric range token (e.g. "5-7", "11-2", "6") into an
 * array of months, handling year wraparound (e.g. "11-2" → [11, 12, 1, 2]).
 *
 * @param {string} token
 * @returns {number[]}
 */
function parseNumericRange(token) {
  const rangeMatch = token.match(/^(\d{1,2})-(\d{1,2})$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    if (start < 1 || start > 12 || end < 1 || end > 12) return [];
    if (start <= end) {
      const months = [];
      for (let m = start; m <= end; m++) months.push(m);
      return months;
    }
    // Year wraparound: e.g. 11-2 → [11, 12, 1, 2]
    const months = [];
    for (let m = start; m <= 12; m++) months.push(m);
    for (let m = 1; m <= end; m++) months.push(m);
    return months;
  }

  const singleMatch = token.match(/^(\d{1,2})$/);
  if (singleMatch) {
    const m = parseInt(singleMatch[1], 10);
    return m >= 1 && m <= 12 ? [m] : [];
  }

  return [];
}

/**
 * Parses a timing string into an array of month numbers.
 *
 * Handles:
 *   - English enum values: "early_spring", "autumn", "anytime_if_needed", …
 *   - German seasonal terms: "Frühling", "Herbst", "Ganzjährig", …
 *   - Numeric ranges: "5-7", "11-2" (with wraparound), "6"
 *   - Comma-separated ranges: "3-4, 11-1"
 *   - Special values that carry no timing info ("none", "after_flowering",
 *     empty string, null, undefined) → []
 *
 * @param {string|null|undefined} text
 * @returns {number[]} Unique month numbers sorted ascending, or []
 */
export function parseGermanTimeRange(text) {
  if (text == null || typeof text !== 'string') return [];
  const trimmed = text.trim().toLowerCase();
  if (!trimmed || trimmed === 'none' || trimmed === 'after_flowering') return [];

  // Exact enum / keyword lookup
  if (TIMING_TO_MONTHS[trimmed]) return [...TIMING_TO_MONTHS[trimmed]];

  // Comma-separated multi-range: "3-4, 11-1"
  if (trimmed.includes(',')) {
    const months = trimmed
      .split(',')
      .flatMap((part) => parseNumericRange(part.trim()));
    // deduplicate and sort
    return [...new Set(months)].sort((a, b) => a - b);
  }

  // Single range or single month (sort so wraparound results are consistent)
  return parseNumericRange(trimmed).sort((a, b) => a - b);
}

/**
 * Returns the care actions that are relevant for a plant in the given month.
 *
 * Checks:
 *   - Pruning (care_instructions.pruning_best_time)
 *       "after_flowering" is handled by shifting the plant's flowering months
 *       forward by one month.
 *   - Planting (care_instructions.planting_time)
 *
 * @param {Object} plant  Plant object from the API
 * @param {number} currentMonth  1–12
 * @returns {{ type: string, label: string, timeRange: string }[]}
 */
export function getPlantCareActions(plant, currentMonth) {
  const actions = [];
  const care = plant.care_instructions || {};

  // --- Pruning ---
  const pruningTime = care.pruning_best_time;
  if (pruningTime && pruningTime !== 'none') {
    let months;
    if (pruningTime === 'after_flowering') {
      // One month after each flowering month (API: plant.properties.flowering_months)
      const floweringMonths = Array.isArray(plant.properties?.flowering_months)
        ? plant.properties.flowering_months
        : [];
      months = floweringMonths.map((m) => (m % 12) + 1);
    } else {
      months = parseGermanTimeRange(pruningTime);
    }
    if (months.includes(currentMonth)) {
      actions.push({
        type: 'pruning',
        label: 'Schnitt',
        timeRange: pruningTime,
      });
    }
  }

  // --- Planting ---
  const plantingTime = care.planting_time;
  if (plantingTime) {
    const months = parseGermanTimeRange(plantingTime);
    if (months.includes(currentMonth)) {
      actions.push({
        type: 'planting',
        label: 'Pflanzzeit',
        timeRange: plantingTime,
      });
    }
  }

  // --- Ernte ---
  const harvestMonths = care.harvest_months;
  if (harvestMonths) {
    const months = parseGermanTimeRange(harvestMonths);
    if (months.includes(currentMonth)) {
      actions.push({ type: 'harvest', label: 'Ernte', timeRange: harvestMonths });
    }
  }

  // --- Mulchen ---
  const mulchTime = care.mulching_best_time;
  if (mulchTime) {
    const months = parseGermanTimeRange(mulchTime);
    if (months.includes(currentMonth)) {
      actions.push({ type: 'mulching', label: 'Mulchen', timeRange: mulchTime });
    }
  }

  // --- Winterschutz ---
  if (care.winter_protection_needed === true) {
    const winterMonths = [10, 11];
    if (winterMonths.includes(currentMonth)) {
      actions.push({
        type: 'winter_protection',
        label: 'Winterschutz',
        timeRange: 'Herbst',
      });
    }
  }

  return actions;
}

/**
 * Maps timing enum/keyword values to human-readable German labels.
 * Used by formatTimeRange().
 */
const TIMING_TO_GERMAN_LABEL = {
  early_spring: 'Frühjahr',
  late_spring: 'Spätfrühling',
  early_summer: 'Frühsommer',
  late_summer: 'Spätsommer',
  summer: 'Sommer',
  autumn: 'Herbst',
  late_autumn: 'Spätherbst',
  late_winter: 'Spätwinter',
  winter: 'Winter',
  spring: 'Frühjahr',
  anytime_if_needed: 'bei Bedarf',
  anytime_frost_free: 'frostfreie Monate',
  anytime: 'ganzjährig',
  after_flowering: 'nach der Blüte',
  frühling: 'Frühjahr',
  frühjahr: 'Frühjahr',
  fruehjahr: 'Frühjahr',
  sommer: 'Sommer',
  herbst: 'Herbst',
  ganzjährig: 'ganzjährig',
  ganzjaehrig: 'ganzjährig',
  immer: 'ganzjährig',
  none: '',
};

const MONTH_NAMES_DE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];

/**
 * Formats a raw timing string from the plant database into a human-readable
 * German label for display in the Pflegekalender.
 *
 * Examples:
 *   "early_spring"   → "Frühjahr"
 *   "after_flowering"→ "nach der Blüte"
 *   "3-5"            → "März–Mai"
 *   "8"              → "August"
 *   "6,9"            → "Juni, September"
 *   "Herbst"         → "Herbst"
 *   null/undefined   → ""
 *
 * @param {string|null|undefined} timeRange  Raw timing value from plant database
 * @returns {string}  Human-readable German label
 */
export function formatTimeRange(timeRange) {
  if (timeRange == null || typeof timeRange !== 'string') return '';
  const trimmed = timeRange.trim();
  if (!trimmed) return '';

  const lower = trimmed.toLowerCase();

  // Exact keyword lookup
  if (TIMING_TO_GERMAN_LABEL[lower] !== undefined) {
    return TIMING_TO_GERMAN_LABEL[lower];
  }

  // Comma-separated: format each part and join
  if (trimmed.includes(',')) {
    return trimmed
      .split(',')
      .map((part) => formatTimeRange(part.trim()))
      .filter(Boolean)
      .join(', ');
  }

  // Numeric range "M-N" → "MonatName–MonatName"
  const rangeMatch = trimmed.match(/^(\d{1,2})-(\d{1,2})$/);
  if (rangeMatch) {
    const start = parseInt(rangeMatch[1], 10);
    const end = parseInt(rangeMatch[2], 10);
    if (start >= 1 && start <= 12 && end >= 1 && end <= 12) {
      if (start === end) return MONTH_NAMES_DE[start - 1];
      return `${MONTH_NAMES_DE[start - 1]}–${MONTH_NAMES_DE[end - 1]}`;
    }
  }

  // Single month number "M"
  const singleMatch = trimmed.match(/^(\d{1,2})$/);
  if (singleMatch) {
    const m = parseInt(singleMatch[1], 10);
    if (m >= 1 && m <= 12) return MONTH_NAMES_DE[m - 1];
  }

  // Fallback: return as-is (e.g. free-text German descriptions)
  return trimmed;
}

/**
 * Computes a grouped care timeline for the Pflegekalender view.
 *
 * Groups placed plants' care actions into three time windows:
 *   current  — the current month
 *   upcoming — the next 2 months (month+1, month+2)
 *   later    — months +3 through +6
 *
 * Within each window, actions are grouped by care type. A plant appears at
 * most once per type per window even if it matches multiple months. Across
 * windows, each (type, plant) combination is emitted only once — in the
 * earliest window it is relevant — so overlapping ranges (e.g. "bei Bedarf",
 * "Aug–Okt") no longer show up duplicated in current AND upcoming/later.
 * Different care types of the same plant stay independent.
 *
 * @param {(string|number)[]} placedPlantIds  IDs of placed plants
 * @param {Object[]}          allPlants       Full plant catalog
 * @param {number}            currentMonth    1–12
 * @returns {{
 *   current:  { type: string, label: string, plants: { plant: Object, timeRange: string }[] }[],
 *   upcoming: { type: string, label: string, plants: { plant: Object, timeRange: string }[] }[],
 *   later:    { type: string, label: string, plants: { plant: Object, timeRange: string }[] }[]
 * }}
 */
export function getCareTimeline(placedPlantIds, allPlants, currentMonth) {
  const ids = placedPlantIds || [];
  const placedPlants = (allPlants || []).filter((p) => ids.includes(p.id));

  function wrapMonth(offset) {
    return ((currentMonth - 1 + offset) % 12) + 1;
  }

  const windows = {
    current: [currentMonth],
    upcoming: [wrapMonth(1), wrapMonth(2)],
    later: [wrapMonth(3), wrapMonth(4), wrapMonth(5), wrapMonth(6)],
  };

  // Tracks `${type}:${plantKey}` combinations already emitted in an earlier
  // window, so a measure spanning several windows appears only in the first.
  const emitted = new Set();

  function buildGroups(months) {
    // type → { type, label, plantsMap: Map<plantKey, {plant, timeRange}> }
    const typeMap = new Map();

    for (const month of months) {
      for (const plant of placedPlants) {
        const actions = getPlantCareActions(plant, month);
        for (const action of actions) {
          const plantKey = plant.id ?? plant.botanical_name;
          const dedupKey = `${action.type}:${plantKey}`;
          if (emitted.has(dedupKey)) continue;
          emitted.add(dedupKey);

          if (!typeMap.has(action.type)) {
            typeMap.set(action.type, {
              type: action.type,
              label: action.label,
              plantsMap: new Map(),
            });
          }
          typeMap.get(action.type).plantsMap.set(plantKey, {
            plant,
            timeRange: action.timeRange,
          });
        }
      }
    }

    // Groups whose plants were all claimed by an earlier window stay out.
    return [...typeMap.values()]
      .map(({ type, label, plantsMap }) => ({
        type,
        label,
        plants: [...plantsMap.values()],
      }))
      .filter((group) => group.plants.length > 0);
  }

  // Fill windows earliest-first so each measure lands in its earliest window.
  return {
    current: buildGroups(windows.current),
    upcoming: buildGroups(windows.upcoming),
    later: buildGroups(windows.later),
  };
}

/**
 * Finds the next month (after currentMonth) in which any of the given plants
 * has a care action. Searches up to 12 months ahead.
 *
 * @param {Object[]} plants  Array of plant objects
 * @param {number}   currentMonth  1–12
 * @returns {number|null}  The next relevant month, or null if none found
 */
export function getNextRelevantMonth(plants, currentMonth) {
  if (!plants || plants.length === 0) return null;

  for (let offset = 1; offset <= 12; offset++) {
    const month = ((currentMonth - 1 + offset) % 12) + 1;
    for (const plant of plants) {
      if (getPlantCareActions(plant, month).length > 0) return month;
    }
  }
  return null;
}

/**
 * Determines the Dashboard state from a list of placed plant IDs and the full
 * plant catalog.
 *
 * State rules:
 *   '0' — no plants placed on canvas
 *   'A' — plants placed, but none in active care phase this month
 *   'B' — at least one plant has an active care phase this month
 *   'F' — COMPLEXITY_THRESHOLD or more plants in an active care phase at once
 *         (high complexity → collective view instead of a per-plant list)
 *
 * @param {(string|number)[]} placedPlantIds  IDs of plants on the canvas
 * @param {Object[]}          allPlants       Full plant catalog from the API
 * @param {number}            currentMonth    1–12
 * @returns {{ state: '0'|'A'|'B'|'F', activePlants: Object[], nextRelevantMonth: number|null }}
 */
export function determineDashboardState(placedPlantIds, allPlants, currentMonth) {
  if (!placedPlantIds || placedPlantIds.length === 0) {
    return { state: '0', activePlants: [], nextRelevantMonth: null };
  }

  const placedPlants = (allPlants || []).filter((p) =>
    placedPlantIds.includes(p.id)
  );

  const activePlants = [];
  for (const plant of placedPlants) {
    const actions = getPlantCareActions(plant, currentMonth);
    if (actions.length > 0) {
      activePlants.push({ plant, actions });
    }
  }

  if (activePlants.length >= COMPLEXITY_THRESHOLD) {
    return { state: 'F', activePlants, nextRelevantMonth: null };
  }

  if (activePlants.length > 0) {
    return { state: 'B', activePlants, nextRelevantMonth: null };
  }

  const nextRelevantMonth = getNextRelevantMonth(placedPlants, currentMonth);
  return { state: 'A', activePlants: [], nextRelevantMonth };
}
